import {
  DownloadOutlined,
  FileExcelOutlined,
  FilterOutlined,
  LeftOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { Button, FormInstance } from 'antd';
import ActionSheet, { SheetAction } from 'components/UI/Responsive/ActionSheet';
import ResponsivePageHeader, {
  HeaderFilterButton,
  HeaderIconButton,
} from 'components/UI/Responsive/ResponsivePageHeader';
import FormFiltersFields from 'model/FormFiltersFields';
import { Order } from 'model/Order';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { sessionSelectors } from 'store/state/selectors';
import {
  countActiveAdvancedFilters,
  describeAppliedPeriod,
} from '../MyOrdersFilter/myOrdersFilterValues';
import { useMyOrdersReport } from '../MyOrdersTable/useMyOrdersReport';

import * as S from './styles';

interface Props {
  form: FormInstance;
  orders: Order[];
  totalCount: number;
  appliedValues?: Partial<FormFiltersFields>;
  onOpenFilters?: () => void;
}

export default function MyOrdersResponsiveHeader({
  form,
  orders,
  totalCount,
  appliedValues,
  onOpenFilters,
}: Props) {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const producerUrl = useSelector(sessionSelectors.getProducerUrl);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const { downloadExcel, print } = useMyOrdersReport(form, orders);
  const period = appliedValues && describeAppliedPeriod(appliedValues);
  const isDisabled = orders.length === 0;

  const downloadActions: SheetAction[] = [
    {
      key: 'excel',
      label: 'Exportar Excel',
      hint: 'Arquivo .xlsx com os filtros atuais',
      icon: <FileExcelOutlined />,
      tone: 'success',
      onSelect: downloadExcel,
    },
    {
      key: 'print',
      label: 'Imprimir',
      hint: 'Pedidos carregados na tela',
      icon: <PrinterOutlined />,
      onSelect: print,
    },
  ];

  return (
    <S.Container>
      <S.BackButton
        type="button"
        onClick={() => navigate(`/${producerUrl}/products`)}
      >
        <LeftOutlined aria-hidden />
        {translate('shoppingCart.continueShopping')}
      </S.BackButton>

      <ResponsivePageHeader
        title={translate('myOrders.title')}
        subtitle={period ?? translate('myOrders.legend')}
      >
        {onOpenFilters ? (
          <>
            <HeaderIconButton
              type="button"
              aria-label="Baixar relatório"
              disabled={isDisabled}
              onClick={() => setIsDownloadOpen(true)}
            >
              <DownloadOutlined aria-hidden />
            </HeaderIconButton>
            <HeaderFilterButton
              type="button"
              aria-label="Filtros"
              data-active={countActiveAdvancedFilters(appliedValues ?? {}) > 0}
              onClick={onOpenFilters}
            >
              <FilterOutlined aria-hidden />
            </HeaderFilterButton>
          </>
        ) : (
          <S.Actions>
            <Button
              icon={<PrinterOutlined aria-hidden />}
              disabled={isDisabled}
              onClick={print}
            >
              Imprimir
            </Button>
            <Button
              type="primary"
              disabled={isDisabled}
              onClick={downloadExcel}
            >
              Exportar Excel
            </Button>
          </S.Actions>
        )}
      </ResponsivePageHeader>

      <ActionSheet
        isOpen={isDownloadOpen}
        onClose={() => setIsDownloadOpen(false)}
        title="Baixar relatório"
        description={`${totalCount.toLocaleString(
          'pt-BR',
        )} itens · filtros atuais aplicados`}
        actions={downloadActions}
      />
    </S.Container>
  );
}
